import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import artmobooksBackground from '../assets/artmobooks.png';
import { db, auth } from '../firebase';
import { collection, addDoc, doc, getDocs, orderBy, query, updateDoc, deleteDoc } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { authFetch } from '../utils/authFetch';
import { backendConfig } from '../config/backendConfig';

const STATUS_OPTIONS = [
  { key: 'in_queue_for_printing', label: 'In queue for printing', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'printing_started', label: 'Printing started', color: 'bg-blue-100 text-blue-800' },
  { key: 'printed', label: 'Printed', color: 'bg-green-100 text-green-800' },
  { key: 'quality_check', label: 'Quality check', color: 'bg-purple-100 text-purple-800' },
  { key: 'packaging', label: 'Packaging', color: 'bg-indigo-100 text-indigo-800' },
  { key: 'ready_for_shipping', label: 'Ready for shipping', color: 'bg-orange-100 text-orange-800' },
  { key: 'shipped', label: 'Shipped', color: 'bg-teal-100 text-teal-800' },
  { key: 'in_transit', label: 'In transit', color: 'bg-cyan-100 text-cyan-800' },
  { key: 'out_for_delivery', label: 'Out for delivery', color: 'bg-pink-100 text-pink-800' },
  { key: 'delivered', label: 'Delivered', color: 'bg-green-200 text-green-900' },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
  { key: 'returned', label: 'Returned', color: 'bg-gray-100 text-gray-800' },
];

export default function AdminOrdersDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [viewMode, setViewMode] = useState('orders'); // 'orders' or 'customers'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [batchShipping, setBatchShipping] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [adminCheckDone, setAdminCheckDone] = useState(false);

  useEffect(() => {
    const user = auth?.currentUser;
    if (!user) {
      setAdminCheckDone(true);
      setIsAllowed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${backendConfig.baseUrl}/api/admin/check-admin?uid=${user.uid}`);
        if (cancelled) return;
        if (!res.ok) {
          setIsAllowed(false);
          setAdminCheckDone(true);
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setIsAllowed(!!data.isAdmin);
          setAdminCheckDone(true);
        }
      } catch (_) {
        if (!cancelled) {
          setIsAllowed(false);
          setAdminCheckDone(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [auth?.currentUser?.uid]);

  useEffect(() => {
    if (!auth?.currentUser) return;
    if (!isAllowed) return;
    setLoading(true);
    const qRef = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(qRef, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      console.log('📊 Orders listener received:', data.length, 'orders');
      setOrders(data);
      setLoading(false);
    }, (err) => {
      console.error('Realtime orders listener error:', err);
      setLoading(false);
    }, { purpose: 'AdminOrdersDashboard-orders', component: 'AdminOrdersDashboard' });
    return () => unsub();
  }, [isAllowed, auth?.currentUser?.uid]);

  const filtered = useMemo(() => {
    return orders.filter(o => {
      let byStatus = true;
      if (filter !== 'all') {
        // Handle group filters
        if (filter === 'in_queue_group') {
          byStatus = ['in_queue_for_printing', 'processing', 'pending'].includes(o.status);
        } else if (filter === 'in_production_group') {
          byStatus = ['printing_started', 'printed', 'quality_check', 'packaging', 'in_production'].includes(o.status);
        } else if (filter === 'in_transit_group') {
          byStatus = ['ready_for_shipping', 'shipped', 'in_transit', 'out_for_delivery', 'on_way'].includes(o.status);
        } else if (filter === 'delivered_group') {
          byStatus = ['delivered', 'completed'].includes(o.status);
        } else {
          // Handle individual status filters
          byStatus = o.status === filter;
        }
      }
      const bySearch = search.trim() ? (
        (o.userEmail || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.items || []).some(i => (i.title || '').toLowerCase().includes(search.toLowerCase())) ||
        (o.id || '').toLowerCase().includes(search.toLowerCase()) ||
        // Search by guest name and phone number
        (o.address?.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (o.address?.phone || '').includes(search) ||
        (o.address?.email || '').toLowerCase().includes(search.toLowerCase())
      ) : true;
      
      // Date filtering
      let byDate = true;
      if (dateFilter !== 'all') {
        const orderDate = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
        const now = new Date();
        const oneDay = 24 * 60 * 60 * 1000;
        
        switch (dateFilter) {
          case 'today':
            byDate = orderDate.toDateString() === now.toDateString();
            break;
          case 'week':
            byDate = (now - orderDate) <= 7 * oneDay;
            break;
          case 'month':
            byDate = (now - orderDate) <= 30 * oneDay;
            break;
          case 'quarter':
            byDate = (now - orderDate) <= 90 * oneDay;
            break;
        }
      }
      
      return byStatus && bySearch && byDate;
    });
  }, [orders, filter, search, dateFilter]);

  // Group orders by customer for batch shipping optimization
  const customerGroups = useMemo(() => {
    const groups = {};
    filtered.forEach(order => {
      const email = order.userEmail || 'guest';
      if (!groups[email]) {
        groups[email] = {
          email,
          orders: [],
          totalOrders: 0,
          totalValue: 0,
          shippingAddress: order.address,
          readyForBatchShipping: false,
          batchShippingId: null
        };
      }
      groups[email].orders.push(order);
      groups[email].totalOrders += 1;
      groups[email].totalValue += order.total || 0;
      
      // Check if all orders are ready for batch shipping
      const allReady = groups[email].orders.every(o => 
        ['ready_for_shipping', 'packaging'].includes(o.status)
      );
      groups[email].readyForBatchShipping = allReady && groups[email].orders.length > 1;
    });
    return groups;
  }, [filtered]);

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return 'Not set';
    if (typeof date === 'string') date = new Date(date);
    if (date instanceof Date) {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return 'Invalid date';
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    return STATUS_OPTIONS.find(s => s.key === status)?.color || 'bg-gray-100 text-gray-800';
  };

  const updateStatus = async (orderId, newStatus) => {
    const currentOrder = orders.find(o => o.id === orderId);
    const newTimelineEntry = {
      key: newStatus,
      label: STATUS_OPTIONS.find(s => s.key === newStatus)?.label || newStatus,
      at: new Date().toISOString(),
      updatedBy: auth?.currentUser?.email || 'admin'
    };

    await updateDoc(doc(db, 'orders', orderId), {
      status: newStatus,
      timeline: [...(currentOrder?.timeline || []), newTimelineEntry],
      [`statusUpdatedAt.${newStatus}`]: new Date().toISOString()
    });

    setOrders(prev => prev.map(o => o.id === orderId ? { 
      ...o, 
      status: newStatus,
      timeline: [...(o.timeline || []), newTimelineEntry],
      statusUpdatedAt: { ...(o.statusUpdatedAt || {}), [newStatus]: new Date().toISOString() }
    } : o));
  };

  // Delete order function
  const deleteOrder = async (orderId) => {
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'orders', orderId));
      
      // Remove from local state
      setOrders(prev => prev.filter(o => o.id !== orderId));
      
      // Close modal if the deleted order was selected
      if (selectedOrder?.id === orderId) {
        closeOrder();
      }
      
      alert(`✅ Order #${orderId.slice(-8).toUpperCase()} deleted successfully`);
    } catch (error) {
      console.error('Failed to delete order:', error);
      alert('❌ Failed to delete order: ' + error.message);
    }
  };

  // Batch shipping functions
  const createBatchShipment = async (customerEmail) => {
    const customer = customerGroups[customerEmail];
    if (!customer || !customer.readyForBatchShipping) return;

    const batchId = `BATCH_${Date.now()}_${customerEmail.slice(0, 8)}`;
    const now = new Date().toISOString();

    try {
      // Update all orders in the batch
      const updatePromises = customer.orders.map(order => {
        const newTimelineEntry = {
          key: 'batch_shipped',
          label: 'Batch Shipped',
          at: now,
          updatedBy: auth?.currentUser?.email || 'admin',
          batchId: batchId
        };

        return updateDoc(doc(db, 'orders', order.id), {
          status: 'shipped',
          timeline: [...(order.timeline || []), newTimelineEntry],
          batchShippingId: batchId,
          batchShippedAt: now,
          [`statusUpdatedAt.shipped`]: now
        });
      });

      await Promise.all(updatePromises);

      // Update local state
      setOrders(prev => prev.map(o => {
        if (customer.orders.some(co => co.id === o.id)) {
          const newTimelineEntry = {
            key: 'batch_shipped',
            label: 'Batch Shipped',
            at: now,
            updatedBy: auth?.currentUser?.email || 'admin',
            batchId: batchId
          };
          return {
            ...o,
            status: 'shipped',
            timeline: [...(o.timeline || []), newTimelineEntry],
            batchShippingId: batchId,
            batchShippedAt: now
          };
        }
        return o;
      }));

      setBatchShipping(prev => ({ ...prev, [customerEmail]: batchId }));
      alert(`✅ Batch shipment created for ${customerEmail} with ID: ${batchId}`);
    } catch (error) {
      console.error('Failed to create batch shipment:', error);
      alert('❌ Failed to create batch shipment: ' + error.message);
    }
  };

  const getBatchShippingSavings = (customerEmail) => {
    const customer = customerGroups[customerEmail];
    if (!customer || customer.orders.length < 2) return 0;
    
    // Calculate shipping cost savings (example: ₹50 per order, ₹100 for batch)
    const individualShipping = customer.orders.length * 50;
    const batchShipping = 100;
    return individualShipping - batchShipping;
  };

  // Order selection functions
  const openOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const closeOrder = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  if (!auth?.currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white/90 p-6 rounded-xl border border-yellow-200 shadow">
          <div className="text-gray-800 font-medium">Please login to access admin dashboard.</div>
          <div className="mt-3 text-right">
            <button onClick={()=>navigate('/login')} className="px-4 py-2 rounded-full bg-yellow-500 hover:bg-yellow-600 text-white">Login</button>
          </div>
        </div>
      </div>
    );
  }

  if (adminCheckDone && !isAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white/90 p-6 rounded-xl border border-yellow-200 shadow text-center">
          <div className="text-gray-800 font-semibold mb-2">Access Restricted</div>
          <div className="text-gray-600 text-sm">Your account is not permitted to view this page.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cover bg-center" style={{ backgroundImage: `url(${artmobooksBackground})` }}>
      <div className="absolute inset-0 bg-black/20" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-6">
        {!adminCheckDone && (
          <div className="mb-4 rounded-xl border border-yellow-200 bg-yellow-50/90 px-4 py-3 text-sm text-yellow-800">
            Checking admin access...
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white drop-shadow">Artmosphere Orders Dashboard</h1>
          <div className="flex gap-3">

            <button onClick={()=>navigate('/')} className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full shadow">Artist Corner</button>
          </div>
        </div>

        {/* Admin status banner */}
        <div className="mb-4">
          <div className={`rounded-xl border p-3 text-sm backdrop-blur-sm ${isAllowed ? 'bg-green-50/90 border-green-200 text-green-800' : 'bg-red-50/90 border-red-200 text-red-800'}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <span className="font-semibold">Signed in as:</span> {auth?.currentUser?.email || 'Unknown'}
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${isAllowed ? 'bg-green-200/70 text-green-900' : 'bg-red-200/70 text-red-900'}`}>
                {isAllowed ? 'Admin access granted' : 'Admin access NOT granted'}
              </div>
            </div>
          </div>
        </div>

        {/* Order Statistics */}
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 backdrop-blur-sm rounded-xl border border-yellow-200 p-6 shadow-lg mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
            <div 
              className={`text-center p-4 rounded-lg border shadow-sm cursor-pointer transition-all duration-200 hover:scale-105 ${
                filter === 'in_queue_group' 
                  ? 'bg-yellow-100 border-yellow-300 shadow-md' 
                  : 'bg-white/70 border-yellow-100 hover:bg-white/90 hover:shadow-md'
              }`}
              onClick={() => setFilter('in_queue_group')}
              title="Click to view In Queue orders"
            >
              <div className="text-3xl font-bold text-yellow-600">
                {orders.filter(o => ['in_queue_for_printing', 'processing', 'pending'].includes(o.status)).length}
              </div>
              <div className="text-sm text-gray-700 font-medium">In Queue</div>
              <div className="text-xs text-yellow-600 mt-1">👆 Click to filter</div>
            </div>
            <div 
              className={`text-center p-4 rounded-lg border shadow-sm cursor-pointer transition-all duration-200 hover:scale-105 ${
                filter === 'in_production_group' 
                  ? 'bg-blue-100 border-blue-300 shadow-md' 
                  : 'bg-white/70 border-yellow-100 hover:bg-white/90 hover:shadow-md'
              }`}
              onClick={() => setFilter('in_production_group')}
              title="Click to view In Production orders"
            >
              <div className="text-3xl font-bold text-blue-600">
                {orders.filter(o => ['printing_started', 'printed', 'quality_check', 'packaging', 'in_production'].includes(o.status)).length}
              </div>
              <div className="text-sm text-gray-700 font-medium">In Production</div>
              <div className="text-xs text-blue-600 mt-1">👆 Click to filter</div>
            </div>
            <div 
              className={`text-center p-4 rounded-lg border shadow-sm cursor-pointer transition-all duration-200 hover:scale-105 ${
                filter === 'in_transit_group' 
                  ? 'bg-teal-100 border-teal-300 shadow-md' 
                  : 'bg-white/70 border-yellow-100 hover:bg-white/90 hover:shadow-md'
              }`}
              onClick={() => setFilter('in_transit_group')}
              title="Click to view In Transit orders"
            >
              <div className="text-3xl font-bold text-teal-600">
                {orders.filter(o => ['ready_for_shipping', 'shipped', 'in_transit', 'out_for_delivery', 'on_way'].includes(o.status)).length}
              </div>
              <div className="text-sm text-gray-700 font-medium">In Transit</div>
              <div className="text-xs text-teal-600 mt-1">👆 Click to filter</div>
            </div>
            <div 
              className={`text-center p-4 rounded-lg border shadow-sm cursor-pointer transition-all duration-200 hover:scale-105 ${
                filter === 'delivered_group' 
                  ? 'bg-green-100 border-green-300 shadow-md' 
                  : 'bg-white/70 border-yellow-100 hover:bg-white/90 hover:shadow-md'
              }`}
              onClick={() => setFilter('delivered_group')}
              title="Click to view Delivered orders"
            >
              <div className="text-3xl font-bold text-green-600">
                {orders.filter(o => ['delivered', 'completed'].includes(o.status)).length}
              </div>
              <div className="text-sm text-gray-700 font-medium">Delivered</div>
              <div className="text-xs text-green-600 mt-1">👆 Click to filter</div>
            </div>
          </div>
          
          {/* Enhanced Filter Status Indicator */}
          <div className="mt-4 p-4 bg-white/50 rounded-lg border border-yellow-100">
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
              {/* Total Orders */}
              <span className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                📋 Total Orders: {filtered.length}
                {filtered.length !== orders.length && (
                  <span className="text-xs text-yellow-600">
                    (of {orders.length})
                  </span>
                )}
              </span>
              
              {/* Status Filter */}
              {filter !== 'all' && (
                <span className="inline-flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-800 rounded-full font-medium">
                  🏷️ Status: {
                    filter === 'in_queue_group' ? 'In Queue' :
                    filter === 'in_production_group' ? 'In Production' :
                    filter === 'in_transit_group' ? 'In Transit' :
                    filter === 'delivered_group' ? 'Delivered' :
                    STATUS_OPTIONS.find(s => s.key === filter)?.label || filter
                  }
                </span>
              )}
              
              {/* Date Filter */}
              {dateFilter !== 'all' && (
                <span className="inline-flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-800 rounded-full font-medium">
                  📅 {dateFilter === 'today' ? 'Today' : 
                       dateFilter === 'week' ? 'This Week' : 
                       dateFilter === 'month' ? 'This Month' : 
                       dateFilter === 'quarter' ? 'This Quarter' : dateFilter}
                </span>
              )}
              
              {/* Search Filter */}
              {search.trim() && (
                <span className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-200 text-yellow-900 rounded-full font-medium">
                  🔍 Search: "{search}"
                </span>
              )}
              
              {/* No Filters Applied */}
              {filter === 'all' && dateFilter === 'all' && !search.trim() && (
                <span className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-full font-medium">
                  ✨ No filters applied - showing all orders
                </span>
              )}
              
              {/* Clear All Filters Button */}
              {(filter !== 'all' || dateFilter !== 'all' || search.trim()) && (
                <button
                  onClick={() => {
                    setFilter('all');
                    setDateFilter('all');
                    setSearch('');
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-red-100 text-red-800 rounded-full font-medium hover:bg-red-200 transition-colors"
                >
                  🗑️ Clear All Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-yellow-200 p-4 shadow mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex bg-yellow-100 rounded-full p-1">
                <button
                  onClick={() => setViewMode('orders')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    viewMode === 'orders' 
                      ? 'bg-white text-yellow-700 shadow-sm' 
                      : 'text-yellow-600 hover:text-yellow-700'
                  }`}
                >
                  📋 Orders View
                </button>
                <button
                  onClick={() => setViewMode('customers')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    viewMode === 'customers' 
                      ? 'bg-white text-yellow-700 shadow-sm' 
                      : 'text-yellow-600 hover:text-yellow-700'
                  }`}
                >
                  👥 Customer Groups
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-700">
              {viewMode === 'orders' 
                ? `Filtered orders: ${filtered.length} / ${orders.length}`
                : `Filtered customer groups: ${Object.keys(customerGroups).length}`
              }
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-yellow-200 p-4 shadow mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <select value={filter} onChange={(e)=>setFilter(e.target.value)} className="px-3 py-2 rounded-full border border-yellow-200 bg-white/80">
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <select value={dateFilter} onChange={(e)=>setDateFilter(e.target.value)} className="px-3 py-2 rounded-full border border-yellow-200 bg-white/80">
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
                <option value="quarter">This quarter</option>
              </select>
              <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search by email, name, phone, item, order id" className="px-3 py-2 rounded-full border border-yellow-200 bg-white/80 w-64" />
            </div>
            <div className="text-sm text-gray-700">
              {viewMode === 'orders' 
                ? `Filtered orders: ${filtered.length}`
                : `Ready for batch shipping: ${Object.values(customerGroups).filter(g => g.readyForBatchShipping).length}`
              }
            </div>
          </div>
        </div>

        {/* Content Display */}
        {viewMode === 'customers' ? (
          /* Customer Groups View */
          <div className="grid gap-6">
            {loading ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-yellow-200 shadow p-8 text-center">
                <div className="text-gray-700 text-lg">Loading customer groups...</div>
              </div>
            ) : Object.keys(customerGroups).length === 0 ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-yellow-200 shadow p-8 text-center">
                <div className="text-gray-700 text-lg">No customer groups found.</div>
              </div>
            ) : (
              Object.entries(customerGroups)
                .filter(([email, group]) => {
                  if (search.trim()) {
                    return email.toLowerCase().includes(search.toLowerCase()) ||
                           group.orders.some(o => o.items?.some(i => i.title?.toLowerCase().includes(search.toLowerCase()))) ||
                           // Search by guest name, phone, and address email
                           group.orders.some(o => 
                             (o.address?.name || '').toLowerCase().includes(search.toLowerCase()) ||
                             (o.address?.phone || '').includes(search) ||
                             (o.address?.email || '').toLowerCase().includes(search.toLowerCase())
                           );
                  }
                  return true;
                })
                .map(([email, group]) => (
                  <div key={email} className="bg-white/95 backdrop-blur-sm rounded-2xl border-2 border-blue-200 shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                    {/* Customer Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200 p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="text-2xl font-bold text-gray-900">
                              👤 {email === 'guest' ? 'Guest Orders' : email}
                              {group.orders.some(o => o.isGuestOrder) && (
                                <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                  Guest Orders
                                </span>
                              )}
                            </div>
                            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                              group.readyForBatchShipping 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {group.readyForBatchShipping ? '🚚 Ready for Batch Shipping' : '⏳ Processing Orders'}
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              {group.totalOrders} orders
                            </div>
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                              ₹{group.totalValue.toLocaleString()}
                            </div>
                            {group.orders.length > 1 && (
                              <div className="flex items-center gap-1 text-green-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Save ₹{getBatchShippingSavings(email)} on shipping
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {group.readyForBatchShipping && (
                            <button
                              onClick={() => createBatchShipment(email)}
                              className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium shadow-sm transition-colors"
                            >
                              🚚 Create Batch Shipment
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Orders Summary */}
                    <div className="p-6">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {group.orders.map(order => (
                          <div 
                            key={order.id} 
                            className="bg-gray-50/50 rounded-xl border border-gray-200 p-4 cursor-pointer hover:bg-gray-100/70 transition-colors"
                            onClick={() => openOrder(order)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-sm font-medium text-gray-900">#{order.id.slice(-8).toUpperCase()}</div>
                              <div className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                {STATUS_OPTIONS.find(s => s.key === order.status)?.label || order.status}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              {(order.items || []).reduce((s,i)=>s+(i.quantity||1),0)} items • ₹{order.total?.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDate(order.createdAt)}
                            </div>
                            <div className="text-xs text-blue-600 mt-2 text-center">
                              👆 Click to view details
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
            )}
          </div>
        ) : (
          /* Orders Grid - Compact Widgets */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {loading ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-yellow-200 shadow p-8 text-center">
                <div className="text-gray-700 text-lg">Loading orders...</div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-yellow-200 shadow p-8 text-center">
                <div className="text-gray-700 text-lg">No orders found.</div>
              </div>
            ) : (
              filtered.map(order => (
                <div 
                  key={order.id} 
                  className="bg-white/95 backdrop-blur-sm rounded-xl border-2 border-yellow-200 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onClick={() => openOrder(order)}
                >
                  {/* Order Widget Header */}
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b-2 border-yellow-200 p-4 rounded-t-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-lg font-bold text-gray-900">#{order.id.slice(-8).toUpperCase()}</div>
                      <div className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)} shadow-sm`}>
                        {STATUS_OPTIONS.find(s => s.key === order.status)?.label?.split(' ')[0] || order.status}
                      </div>
                    </div>
                    
                    {/* Customer Info */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="truncate">{order.userEmail || 'Guest'}</span>
                      {order.isGuestOrder && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                          👤 Guest
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="p-4 space-y-3">
                    {/* Order Type & Amount */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {order.printMode ? 'Print' : 'Standard'}
                      </div>
                      <div className="text-lg font-bold text-gray-900">₹{order.total?.toLocaleString()}</div>
                    </div>

                    {/* Items Count */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      {(order.items || []).reduce((s,i)=>s+(i.quantity||1),0)} items
                    </div>

                    {/* Order Date */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(order.createdAt)}
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                      {/* Status Update Dropdown */}
                      <select 
                        value={order.status} 
                        onChange={(e) => {
                          e.stopPropagation();
                          updateStatus(order.id, e.target.value);
                        }} 
                        className="flex-1 px-2 py-1 text-xs rounded border border-yellow-300 bg-white/90 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                      
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete order #${order.id.slice(-8).toUpperCase()}?`)) {
                            deleteOrder(order.id);
                          }
                        }}
                        className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        title="Delete Order"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Click to Expand Hint */}
                    <div className="text-center pt-2">
                      <div className="text-xs text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                        👆 Click to view full details
                      </div>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
        )}

        {/* Order Detail Modal */}
        {showOrderModal && selectedOrder && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-yellow-200 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Order #{selectedOrder.id.slice(-8).toUpperCase()}</h2>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-gray-600">{selectedOrder.userEmail || 'Guest'}</p>
                      {selectedOrder.isGuestOrder && (
                        <span className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full font-medium">
                          👤 Guest Order
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Delete Order Button */}
                    <button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete order #${selectedOrder.id.slice(-8).toUpperCase()}? This action cannot be undone.`)) {
                          deleteOrder(selectedOrder.id);
                        }
                      }}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-all duration-200 hover:scale-110"
                      title="Delete Order"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    
                    {/* Close Modal Button */}
                    <button
                      onClick={closeOrder}
                      className="p-2 hover:bg-yellow-100 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Left Column - Order Details */}
                  <div className="space-y-4">
                    {/* Status Update */}
                    <div className="bg-blue-50/50 rounded-xl border border-blue-200 p-4">
                      <div className="text-sm font-semibold text-gray-800 mb-3">Update Status</div>
                      <select 
                        value={selectedOrder.status} 
                        onChange={(e) => {
                          updateStatus(selectedOrder.id, e.target.value);
                          closeOrder();
                        }} 
                        className="w-full px-3 py-2 rounded-lg border border-blue-300 bg-white text-sm"
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Order Information */}
                    <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-4">
                      <div className="text-sm font-semibold text-gray-800 mb-3">Order Information</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Order Date:</span>
                          <span className="text-gray-900 font-medium">{formatDate(selectedOrder.createdAt)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="text-gray-900 font-medium">₹{selectedOrder.total?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Order Type:</span>
                          <span className="text-gray-900 font-medium">{selectedOrder.printMode ? 'Print Order' : 'Standard Order'}</span>
                        </div>
                        {selectedOrder.isGuestOrder && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Guest Order ID:</span>
                            <span className="text-gray-900 font-medium bg-orange-100 px-2 py-1 rounded text-xs">
                              {selectedOrder.guestOrderId}
                            </span>
                          </div>
                        )}
                        {selectedOrder.batchShippingId && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Batch ID:</span>
                            <span className="text-gray-900 font-medium">{selectedOrder.batchShippingId}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    {selectedOrder.address && (
                      <div className="bg-green-50/50 rounded-xl border border-green-200 p-4">
                        <div className="text-sm font-semibold text-gray-800 mb-3">Delivery Address</div>
                        <div className="text-sm text-gray-700">
                          <div className="font-medium text-gray-900 mb-1">{selectedOrder.address.name}</div>
                          <div className="text-gray-600">{selectedOrder.address.line1}{selectedOrder.address.line2 ? `, ${selectedOrder.address.line2}` : ''}</div>
                          <div className="text-gray-600">{selectedOrder.address.city}, {selectedOrder.address.state} {selectedOrder.address.postalCode}</div>
                          <div className="text-gray-600">{selectedOrder.address.country}</div>
                          <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-green-200">
                            📧 {selectedOrder.address.email} • 📞 {selectedOrder.address.phone}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Items & Timeline */}
                  <div className="space-y-4">
                    {/* Items */}
                    <div className="bg-yellow-50/50 rounded-xl border border-yellow-200 p-4">
                      <div className="text-sm font-semibold text-gray-800 mb-3">Order Items</div>
                      <div className="space-y-3">
                        {(selectedOrder.items || []).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white/70 rounded-lg border border-yellow-100">
                            <div className="flex items-center gap-3">
                              <img src={item.image} alt={item.title} className="w-12 h-12 rounded-lg object-cover border-2 border-yellow-200 shadow-sm" />
                              <div>
                                <div className="font-medium text-gray-900">{item.title}</div>
                                <div className="text-sm text-gray-600">Qty: {item.quantity || 1} • ₹{item.price} each</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">₹{((item.price||0) * (item.quantity||1)).toLocaleString()}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Timeline */}
                    <div className="bg-purple-50/50 rounded-xl border border-purple-200 p-4">
                      <div className="text-sm font-semibold text-gray-800 mb-3">Order Timeline</div>
                      <div className="space-y-2">
                        {(selectedOrder.timeline || []).map((entry, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-white/70 rounded-lg border border-purple-100">
                            <div className={`w-3 h-3 rounded-full ${getStatusColor(entry.key)}`}></div>
                            <div className="flex-1">
                              <span className="font-medium text-gray-900">{entry.label}</span>
                              {entry.updatedBy && (
                                <span className="text-xs text-gray-500 ml-2">by {entry.updatedBy}</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-600 bg-white/80 px-2 py-1 rounded-full">
                              {formatDate(entry.at)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


